/**
 * DestroyContainerModal Component
 *
 * A confirmation modal for destroying a tenant's container environment.
 * Requires slug confirmation for safety.
 *
 * @module features/tenants/components/modals/DestroyContainerModal
 */

import { AlertIcon } from '../../../../components/common/Icons';
import type { Tenant } from '../../types';

interface DestroyContainerModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** The tenant whose containers will be destroyed */
  tenant: Tenant | null;
  /** Callback to close the modal */
  onClose: () => void;
  /** Callback to execute container destruction */
  onConfirm: () => void;
  /** Current slug confirmation input value */
  slugConfirmation: string;
  /** Callback when slug confirmation changes */
  onSlugChange: (value: string) => void;
  /** Whether destruction is in progress */
  isDestroying: boolean;
}

/**
 * Renders a confirmation modal for container destruction.
 */
export const DestroyContainerModal: React.FC<DestroyContainerModalProps> = ({
  isOpen,
  tenant,
  onClose,
  onConfirm,
  slugConfirmation,
  onSlugChange,
  isDestroying,
}) => {
  if (!isOpen || !tenant) {
    return null;
  }

  const slugMatches = slugConfirmation === tenant.slug;

  const handleClose = () => {
    onSlugChange('');
    onClose();
  };

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div
        className="glass-card"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: '500px', width: '90%', padding: '24px' }}
      >
        <h2 style={{ color: 'var(--error)', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <AlertIcon size={24} />
          Destroy Container Environment
        </h2>

        <p style={{ marginBottom: '12px' }}>
          You are about to destroy the container environment for:
        </p>

        <div style={{ background: 'rgba(0,0,0,0.3)', padding: '12px 16px', borderRadius: '8px', marginBottom: '16px' }}>
          <div style={{ fontWeight: 600, fontSize: '16px' }}>{tenant.name}</div>
          <code style={{ fontSize: '13px', opacity: 0.7 }}>{tenant.slug}</code>
        </div>

        <div style={{ background: 'rgba(239, 68, 68, 0.15)', border: '1px solid rgba(239, 68, 68, 0.5)', padding: '16px', borderRadius: '8px', marginBottom: '20px' }}>
          <div style={{ fontWeight: 600, marginBottom: '8px' }}>WARNING: This will:</div>
          <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '14px' }}>
            <li>Stop all running containers</li>
            <li>Delete all container data and volumes</li>
            <li>Remove network configuration</li>
            <li>Delete tenant secrets</li>
          </ul>
        </div>

        <div>
          <label style={{ display: 'block', marginBottom: '8px' }}>
            Type <code style={{ background: 'rgba(0,0,0,0.4)', padding: '2px 8px', borderRadius: '4px' }}>
              {tenant.slug}
            </code> to confirm:
          </label>
          <input
            type="text"
            className="input"
            value={slugConfirmation}
            onChange={(e) => onSlugChange(e.target.value)}
            placeholder={tenant.slug}
            style={{ width: '100%', fontFamily: 'monospace' }}
            autoFocus
          />
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '20px' }}>
          <button className="btn btn-secondary" onClick={handleClose}>
            Cancel
          </button>
          <button
            className="btn btn-error"
            onClick={onConfirm}
            disabled={!slugMatches || isDestroying}
            style={{ opacity: slugMatches ? 1 : 0.5 }}
          >
            {isDestroying ? 'Destroying...' : 'Destroy Containers'}
          </button>
        </div>
      </div>
    </div>
  );
};
